import resRequestLinkIconUrl from "../dashboard/icons/resrequest-link.svg"
import type { VacationSearchResult } from "./findVacationsData"

type VacationSearchResultsProps = {
  error: string | null
  hasMoreResults: boolean
  hasSearched: boolean
  isLoading: boolean
  onShowMore: () => void
  results: readonly VacationSearchResult[]
}

export function VacationSearchResults({
  error,
  hasMoreResults,
  hasSearched,
  isLoading,
  onShowMore,
  results,
}: VacationSearchResultsProps) {
  const showSummary = hasSearched && results.length > 0

  return (
    <div aria-live="polite" className="grid gap-3">
      {error ? <p className="m-0 rounded-md border border-[#e0b8b8] bg-[#fff7f7] px-4 py-3 text-sm text-[#8a2d2d]">{error}</p> : null}

      {hasSearched && results.length === 0 && !isLoading ? (
        <p className="m-0 rounded-md border border-[#d7d9dc] bg-white px-4 py-6 text-center text-sm text-[#6f7782]">
          No matching vacations found.
        </p>
      ) : null}

      {results.length > 0 ? (
        <div className="grid grid-cols-3 items-start gap-3 max-[1100px]:grid-cols-2 max-[720px]:grid-cols-1">
          {results.map((result) =>
            result.profile_url ? (
              <div className="relative min-w-0" key={result.id}>
                <a
                  aria-label={`Open ${result.party_name} vacation profile`}
                  className="block min-w-0 rounded-md no-underline transition duration-150 hover:-translate-y-0.5 hover:shadow-[0_10px_24px_rgba(44,52,65,0.12)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#337ab7] focus-visible:ring-offset-2"
                  href={result.profile_url}
                >
                  <VacationCard hasResRequestLink={Boolean(result.resrequest_reservation_url)} result={result} />
                </a>
                <ResRequestLink href={result.resrequest_reservation_url} />
              </div>
            ) : (
              <div className="relative min-w-0" key={result.id}>
                <VacationCard hasResRequestLink={Boolean(result.resrequest_reservation_url)} result={result} />
                <ResRequestLink href={result.resrequest_reservation_url} />
              </div>
            ),
          )}
        </div>
      ) : null}

      {showSummary ? (
        <div className="flex items-center justify-between gap-3 max-[520px]:flex-col max-[520px]:items-start">
          <p className="m-0 text-xs font-semibold leading-[18px] text-[#5f6772]">Showing results 1-{results.length}</p>
          {hasMoreResults ? (
            <button
              className="min-h-9 rounded-md border border-[#c7cbd0] bg-white px-4 text-xs font-semibold text-[#3f4650] hover:bg-[#f6f7f8] focus:bg-[#f6f7f8] focus:outline-none disabled:cursor-wait disabled:text-[#89919a]"
              disabled={isLoading}
              onClick={onShowMore}
              type="button"
            >
              Show more
            </button>
          ) : null}
        </div>
      ) : null}

      {isLoading && hasSearched ? <p className="m-0 text-xs leading-[18px] text-[#6f7782]">Loading vacations...</p> : null}
    </div>
  )
}

function VacationCard({ hasResRequestLink, result }: { hasResRequestLink: boolean; result: VacationSearchResult }) {
  const visibleStays = result.stays.slice(0, 3)
  const stayRows = [...Array(3 - visibleStays.length).fill(null), ...visibleStays]

  return (
    <article
      aria-label={`${result.party_name} vacation result`}
      className={`min-w-0 rounded-md border border-[#d7d9dc] bg-white p-4 shadow-[0_1px_2px_rgba(44,52,65,0.05)] ${hasResRequestLink ? "pr-12" : ""}`}
    >
      <div className="flex min-h-[72px] items-start justify-between gap-3">
        <div className="grid min-w-0 gap-2">
          <h2 className="m-0 truncate text-[16px] font-bold leading-5 text-[#2f3742]" title={result.party_name}>
            {result.party_name}
          </h2>
          <p className="m-0 w-fit rounded bg-[#eef3f1] px-2 py-1 text-xs font-semibold leading-4 text-[#426256]">{result.travel_months}</p>
          {result.stays.length > 0 ? (
            <div className="grid min-w-0 grid-rows-3 gap-1.5">
              {stayRows.map((stay, index) => {
                if (!stay) return <div aria-hidden="true" className="h-[26px]" key={`empty-stay-${index}`} />

                const label = `${stay.name} - ${stay.date_range}`
                const remainingStayCount = result.stays.length - 3

                return (
                  <div className="flex min-w-0 gap-1.5" key={stay.id}>
                    <span
                      className="min-w-0 truncate rounded border border-[#d8dde1] px-2 py-1 text-xs leading-4 text-[#5f6772]"
                      title={label}
                    >
                      {label}
                    </span>
                    {stay === visibleStays[2] && remainingStayCount > 0 ? (
                      <span className="shrink-0 rounded border border-[#d8dde1] px-2 py-1 text-xs leading-4 text-[#5f6772]">
                        +{remainingStayCount} stays
                      </span>
                    ) : null}
                  </div>
                )
              })}
            </div>
          ) : null}
        </div>

        <div className="flex shrink-0 items-center gap-2">
          {result.profile_url ? null : <span className="text-xs font-semibold leading-4 text-[#8a929c]">No profile available</span>}
        </div>
      </div>
    </article>
  )
}

function ResRequestLink({ href }: { href?: string | null }) {
  if (!href) return null

  return (
    <a
      aria-label="Open ResRequest"
      className="absolute right-4 top-4 z-10 grid h-6 w-6 shrink-0 place-items-center rounded text-[#495b6a] hover:bg-[#f6f7f8] hover:text-[#1d262e] focus:outline focus:outline-2 focus:outline-offset-2 focus:outline-[#495b6a]"
      href={href}
      rel="noreferrer"
      target="_blank"
      title="ResRequest"
    >
      <img alt="" className="block h-3.5 w-3.5" src={resRequestLinkIconUrl} />
    </a>
  )
}
